/**
 * Lawyer-friendly names for MIME content types. Kept in its own file
 * (no React / Redux / entities imports) so unit tests can pull it in
 * without bootstrapping the whole document graph.
 *
 * Re-exported from documentHelper.ts for callers that already import
 * from there.
 */

const FRIENDLY_CONTENT_TYPE: Record<string, string> = {
    'application/pdf': 'PDF',

    // Word processor
    'application/msword': 'Word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.oasis.opendocument.text': 'Word',
    'application/rtf': 'Word',
    'application/x-abiword': 'Word',

    // Spreadsheet
    'application/vnd.ms-excel': 'Spreadsheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Spreadsheet',
    'application/vnd.oasis.opendocument.spreadsheet': 'Spreadsheet',
    'text/csv': 'Spreadsheet',

    // Presentation
    'application/vnd.ms-powerpoint': 'Presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Presentation',
    'application/vnd.oasis.opendocument.presentation': 'Presentation',

    // Email
    'message/rfc822': 'Email',
    'application/vnd.ms-outlook': 'Email',

    // Web / markup
    'text/html': 'HTML',
    'application/xhtml+xml': 'HTML',
    'application/json': 'JSON',
    'application/ld+json': 'JSON',
    'text/xml': 'XML',
    'application/xml': 'XML',
    'application/vnd.mozilla.xul+xml': 'XML',

    // Calendar
    'text/calendar': 'Calendar',

    // Ebook
    'application/vnd.amazon.ebook': 'Ebook',
    'application/epub+zip': 'Ebook',

    // Archives
    'application/zip': 'Archive',
    'application/x-7z-compressed': 'Archive',
    'application/x-rar-compressed': 'Archive',
    'application/x-bzip': 'Archive',
    'application/x-bzip2': 'Archive',
    'application/x-tar': 'Archive',
    'application/gzip': 'Archive',
    'application/java-archive': 'Archive',
    'application/x-freearc': 'Archive',

    // Scripts (sh / csh / php — all present in MIME_TYPES)
    'application/x-sh': 'Script',
    'application/x-csh': 'Script',
    'application/php': 'Script',

    // Fonts (the MS-specific one that isn't under font/*)
    'application/vnd.ms-fontobject': 'Font',

    // Installer
    'application/vnd.apple.installer+xml': 'Installer',

    // Diagram
    'application/vnd.visio': 'Diagram',

    // Flash
    'application/x-shockwave-flash': 'Flash',

    // Generic OGG container (mixed audio/video — most often audio)
    'application/ogg': 'Audio',

    // Catch-all binary blob
    'application/octet-stream': 'Binary',
};

export function friendlyContentType(contentType: string | null | undefined): string {
    if (!contentType) return '';
    const ct = contentType.toLowerCase().trim();
    if (!ct) return '';

    const known = FRIENDLY_CONTENT_TYPE[ct];
    if (known) return known;

    // Family-level fallbacks for any type/subtype we haven't enumerated.
    if (ct.startsWith('image/')) return 'Image';
    if (ct.startsWith('audio/')) return 'Audio';
    if (ct.startsWith('video/')) return 'Video';
    if (ct.startsWith('font/')) return 'Font';
    if (ct.startsWith('text/')) return 'Text';

    return contentType;  // unknown — show the raw value so info isn't lost
}
